/**
 * useFocusAreaCheckIn
 *
 * React Query hooks and custom streaming hook for focus area check-in sessions.
 * Provides: useStartCheckIn, useFinalizeCheckIn, useSaveCheckIn,
 * useCheckIn, useCheckInHistory, useOverdueCheckIns, useStreamCheckInMessage.
 */

import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  finalizeCheckIn,
  getCheckIn,
  listCheckIns,
  listOverdueCheckIns,
  startCheckIn,
  streamCheckInMessage,
  updateCheckIn,
} from "../services/focusAreas";
import type { CheckInStreamingCallbacks } from "../services/focusAreas";
import type {
  FocusAreaCheckIn,
  FocusAreaCheckInFinalizeResponse,
  FocusAreaCheckInStartResponse,
  FocusAreaCheckInUpdate,
  FocusAreaCheckInHistoryResponse,
} from "../types/focusAreaCheckIn";
import type { FocusArea } from "../types/focusArea";
import { FOCUS_AREAS_QUERY_KEY } from "./useFocusAreas";

const CHECK_IN_QUERY_KEY = ["checkIns"] as const;

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useStartCheckIn() {
  const qc = useQueryClient();
  return useMutation<FocusAreaCheckInStartResponse, Error, string>({
    mutationFn: (focusAreaId) => startCheckIn(focusAreaId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useFinalizeCheckIn() {
  const qc = useQueryClient();
  return useMutation<FocusAreaCheckInFinalizeResponse, Error, string>({
    mutationFn: (checkInId) => finalizeCheckIn(checkInId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHECK_IN_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

export function useSaveCheckIn() {
  const qc = useQueryClient();
  return useMutation<
    FocusAreaCheckIn,
    Error,
    { checkInId: string; payload: FocusAreaCheckInUpdate }
  >({
    mutationFn: ({ checkInId, payload }) => updateCheckIn(checkInId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHECK_IN_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: FOCUS_AREAS_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useCheckIn(checkInId: string | undefined) {
  return useQuery<FocusAreaCheckIn, Error>({
    queryKey: [...CHECK_IN_QUERY_KEY, checkInId],
    queryFn: () => getCheckIn(checkInId!),
    enabled: !!checkInId,
  });
}

export function useCheckInHistory(focusAreaId: string | undefined) {
  return useQuery<FocusAreaCheckInHistoryResponse, Error>({
    queryKey: [...CHECK_IN_QUERY_KEY, "history", focusAreaId],
    queryFn: () => listCheckIns(focusAreaId!),
    enabled: !!focusAreaId,
  });
}

export function useOverdueCheckIns() {
  return useQuery<FocusArea[], Error>({
    queryKey: [...FOCUS_AREAS_QUERY_KEY, "overdue"],
    queryFn: listOverdueCheckIns,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ---------------------------------------------------------------------------
// Streaming hook
// ---------------------------------------------------------------------------

interface StreamCheckInMessageOptions {
  checkInId: string;
  message: string;
  callbacks: CheckInStreamingCallbacks;
}

export function useStreamCheckInMessage() {
  const qc = useQueryClient();
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const abortRef = useRef<boolean>(false);

  const reset = useCallback(() => {
    setStreamingContent("");
    setIsStreaming(false);
  }, []);

  const start = useCallback(
    async ({ checkInId, message, callbacks }: StreamCheckInMessageOptions) => {
      abortRef.current = false;
      setIsStreaming(true);
      setStreamingContent("");

      try {
        await streamCheckInMessage(checkInId, message, {
          onContent: (content) => {
            if (!abortRef.current) {
              setStreamingContent((prev) => prev + content);
              callbacks.onContent?.(content);
            }
          },
          onDone: (data) => {
            if (!abortRef.current) {
              setIsStreaming(false);
              void qc.invalidateQueries({
                queryKey: [...CHECK_IN_QUERY_KEY, checkInId],
              });
              callbacks.onDone?.(data);
            }
          },
          onError: (error) => {
            if (!abortRef.current) {
              setIsStreaming(false);
              callbacks.onError?.(error);
            }
          },
        });
      } catch (e) {
        if (!abortRef.current) {
          setIsStreaming(false);
          const msg = e instanceof Error ? e.message : "Streaming failed";
          callbacks.onError?.(msg);
        }
      }
    },
    [qc]
  );

  return { start, isStreaming, streamingContent, reset };
}
